import React from 'react';
import {
    ColumnDirective,
    ColumnsDirective,
    Filter,
    FilterSettingsModel,
    GridComponent,
    Inject,
    Page,
    PageSettingsModel,
    Sort,
    SortSettingsModel,
} from '@syncfusion/ej2-react-grids';
import MediaGalleryBulkSelectionToolbar from './MediaGalleryBulkSelectionToolbar';
import ComponentHelpers from '../../helpers/ComponentHelpers';

export interface MediaGalleryRow {
    ID: string;
    Name: string;
    OntologyID: string;
    ThumbnailImageUrl: string;
    North: number;
    East: number;
    Up: number;
    Angle: number | null;
    MediaKind: 'scanPositions' | 'image360';
    View: IView360Detail;
}

interface MediaGalleryGridProps {
    rows: MediaGalleryRow[];
    selectedMediaIdSet: ReadonlySet<string>;
    selectedCount: number;
    allRowsSelected: boolean;
    hasSomeRowsSelected: boolean;
    onOpenView: (view: IView360Detail) => void;
    onSelectView: (view: IView360Detail | null, mediaKind?: 'scanPositions' | 'image360') => void;
    onToggleMediaSelection: (mediaId: string) => void;
    onToggleSelectAllMedia: () => void;
    onClearSelection: () => void;
    onOpenBatchEditor: () => void;
    onMarkSelectedColor: () => void | Promise<void>;
    onMarkSelectedBlackAndWhite: () => void | Promise<void>;
    onDeleteSelectedViews: () => void | Promise<void>;
    canUpdateSelectedViewsMetadata: boolean;
    canSetSelectedPanoColorType: boolean;
    canDeleteSelectedViews: boolean;
}

const filterSettings: FilterSettingsModel = {
    type: 'Excel'
};

const sortSettings: SortSettingsModel = {
    columns: [{ field: 'Name', direction: 'Ascending' }],
    allowUnsort: false
};

const renderCoordinateCell = (value: number | null, row: MediaGalleryRow, digits: number) => {
    const cellClassName = row.MediaKind === 'scanPositions'
        ? 'media-gallery-grid-coordinate media-gallery-grid-coordinate-disabled'
        : 'media-gallery-grid-coordinate';
    const displayValue = value == null ? 'N/A' : value.toFixed(digits);

    return (
        <span className={cellClassName} title={displayValue}>{displayValue}</span>
    );
};

const MediaGalleryGrid: React.FC<MediaGalleryGridProps> = ({
    rows,
    selectedMediaIdSet,
    selectedCount,
    allRowsSelected,
    hasSomeRowsSelected,
    onOpenView,
    onSelectView,
    onToggleMediaSelection,
    onToggleSelectAllMedia,
    onClearSelection,
    onOpenBatchEditor,
    onMarkSelectedColor,
    onMarkSelectedBlackAndWhite,
    onDeleteSelectedViews,
    canUpdateSelectedViewsMetadata,
    canSetSelectedPanoColorType,
    canDeleteSelectedViews
}) => {
    const gridRef = React.useRef<GridComponent | null>(null);
    const pageSettings = React.useMemo<PageSettingsModel>(() => ({
        pageSize: 24,
        pageSizes: [12, 24, 48]
    }), []);
    const hasModifyMediaFilesPermission = React.useMemo(() => ComponentHelpers.hasModifyMediaFilesPermission(), []);
    const hasManageMediaFilesPermission = React.useMemo(() => ComponentHelpers.hasManageMediaFilesPermission(), []);

    React.useEffect(() => {
        if (gridRef.current) {
            const grid = gridRef.current;
            grid.refreshColumns();
            grid.refresh();
        }
    }, [allRowsSelected, canDeleteSelectedViews, canSetSelectedPanoColorType, canUpdateSelectedViewsMetadata, hasSomeRowsSelected, rows, selectedCount, selectedMediaIdSet]);

    const renderNameHeader = React.useCallback(() => {
        return (
            <div className="media-gallery-grid-name-header">
                {(hasModifyMediaFilesPermission || hasManageMediaFilesPermission) && <div className="media-gallery-selection-checkbox-cell media-gallery-selection-checkbox-cell-header">
                    <input
                        type="checkbox"
                        className="media-gallery-selection-checkbox"
                        checked={allRowsSelected}
                        aria-label={InovxLoader.t(allRowsSelected ? 'facility:mediaGallery.clearAllSelected' : 'facility:mediaGallery.selectAll')}
                        ref={(element) => {
                            if (element) {
                                element.indeterminate = hasSomeRowsSelected && !allRowsSelected;
                            }
                        }}
                        onChange={() => onToggleSelectAllMedia()}
                    />
                </div>}
                <span>{InovxLoader.t('facility:mediaGallery.title')}</span>
            </div>
        );
    }, [allRowsSelected, hasSomeRowsSelected, onToggleSelectAllMedia]);

    const renderNameColumn = React.useCallback((row: MediaGalleryRow) => {
        const iconClass = row.MediaKind === 'scanPositions'
            ? 'media-gallery-type-icon media-gallery-type-icon-scan'
            : 'media-gallery-type-icon media-gallery-type-icon-image';
        const isSelected = selectedMediaIdSet.has(row.ID);

        return (
            <span className="media-gallery-grid-name-cell" title={row.Name}>
                {(hasModifyMediaFilesPermission || hasManageMediaFilesPermission) && <span className="media-gallery-selection-checkbox-cell">
                    <input
                        type="checkbox"
                        className="media-gallery-selection-checkbox"
                        checked={isSelected}
                        aria-label={`Select ${row.Name}`}
                        onClick={(event) => event.stopPropagation()}
                        onChange={(event) => {
                            event.stopPropagation();
                            onToggleMediaSelection(row.ID);
                        }}
                    />
                </span>}
                <span className="media-gallery-grid-name" title={row.Name}>
                    <span className={iconClass} aria-hidden="true"></span>
                    <span className="media-gallery-grid-name-text">{row.Name}</span>
                </span>
            </span>
        );
    }, [onToggleMediaSelection, selectedMediaIdSet]);

    const renderActionHeader = React.useCallback(() => {
        return (
            <div className="media-gallery-grid-header-actions">
                <MediaGalleryBulkSelectionToolbar
                    key={selectedCount}
                    selectedCount={selectedCount}
                    canUpdateMetadata={canUpdateSelectedViewsMetadata}
                    canSetPanoColorType={canSetSelectedPanoColorType}
                    canDelete={canDeleteSelectedViews}
                    onClearSelection={onClearSelection}
                    onUpdateMetadata={onOpenBatchEditor}
                    onMarkColor={onMarkSelectedColor}
                    onMarkBlackAndWhite={onMarkSelectedBlackAndWhite}
                    onDeleteMedia={onDeleteSelectedViews}
                />
            </div>
        );
    }, [canDeleteSelectedViews, canSetSelectedPanoColorType, canUpdateSelectedViewsMetadata, onClearSelection, onDeleteSelectedViews, onMarkSelectedBlackAndWhite, onMarkSelectedColor, onOpenBatchEditor, selectedCount]);

    const renderActionColumn = React.useCallback((row: MediaGalleryRow) => {
        return (
            <div className="kb-action-buttons media-gallery-grid-actions">
                <button
                    type="button"
                    className="e-btn e-flat kb-action-view"
                    title={InovxLoader.t('facility:mediaGallery.view')}
                    onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        onSelectView(row.View, row.MediaKind);
                        onOpenView(row.View);
                    }}
                >
                    <span className="e-btn-icon e-f-view e-v-icons e-icons"></span>
                </button>
            </div>
        );
    }, [onOpenView, onSelectView]);

    const handleRecordClick = React.useCallback((args: any) => {
        const target = args?.target as HTMLElement | undefined;
        if (target?.closest('button, input, label')) {
            return;
        }

        const row = args?.rowData as MediaGalleryRow | undefined;
        if (!row) {
            return;
        }

        onSelectView(row.View, row.MediaKind);
    }, [onSelectView]);

    return (
        <div className="media-gallery-grid-shell">
            <div className="table-selection-filter">
                {renderActionHeader()}
            </div>
            <GridComponent
                ref={gridRef}
                className="media-gallery-grid"
                dataSource={rows}
                allowSorting={true}
                allowFiltering={rows.length > 0}
                allowPaging={true}
                filterSettings={filterSettings}
                sortSettings={sortSettings}
                pageSettings={pageSettings}
                allowSelection={false}
                height="100%"
                locale={typeof InovxLoader !== 'undefined' ? InovxLoader.getSyncfusionLocale() : 'en-US'}
                recordClick={handleRecordClick}
            >
                <ColumnsDirective>
                    <ColumnDirective field="Name" headerText={InovxLoader.t('facility:mediaGallery.name')} width="36%" headerTemplate={renderNameHeader} template={renderNameColumn} />
                    <ColumnDirective field="North" headerText={InovxLoader.t('facility:mediaGallery.north')} textAlign="Right" width="12%" template={(row: MediaGalleryRow) => renderCoordinateCell(row.North, row, 3)} />
                    <ColumnDirective field="East" headerText={InovxLoader.t('facility:mediaGallery.east')} textAlign="Right" width="12%" template={(row: MediaGalleryRow) => renderCoordinateCell(row.East, row, 3)} />
                    <ColumnDirective field="Up" headerText={InovxLoader.t('facility:mediaGallery.up')} textAlign="Right" width="12%" template={(row: MediaGalleryRow) => renderCoordinateCell(row.Up, row, 3)} />
                    <ColumnDirective field="Angle" headerText={InovxLoader.t('facility:mediaGallery.angle')} textAlign="Right" width="10%" template={(row: MediaGalleryRow) => renderCoordinateCell(row.Angle, row, 1)} />
                    <ColumnDirective headerText="" width="300" allowFiltering={false} allowSorting={false} template={renderActionColumn} />
                </ColumnsDirective>
                <Inject services={[Sort, Filter, Page]} />
            </GridComponent>
        </div>
    );
};

export default MediaGalleryGrid;
